import hasDom from 'has-dom';
import deepmerge from 'deepmerge';
import _get from 'lodash/get';
import { DataLayerTransformer, inventoryTransformer, QueryStringTransformer, ownerTransformer, listingTypeUtils } from '../../transformers/pixall/index.js';
import { cleanValues, transformCoxAutoDataLayer } from '../../utilities/index.js';
import createCoxAutoDataLayer from './createCoxAutoDataLayer';
import ensurePixallPageLoadAndSendEvent from './ensurePixallPageLoadAndSendEvent';
import getZipCodes from '../../handlers/helpers/getZipCodes.js';

// we don't want to fire meta-data event when we navigate between pages in SPA
// it should re-initialize Pixall instead
let isInitial = true;
export async function transformData(_ref) {
  let {
    filterValues,
    inventory,
    owner,
    pageType,
    consumerMetadata = {},
    dataLayer
  } = _ref;
  return cleanValues({
    ...inventoryTransformer.getInventoryData(inventory, pageType),
    ...QueryStringTransformer.getSearchFacetVariables(filterValues),
    ...ownerTransformer.getOwnerData(owner),
    // deepmerge customized consumerMetadata and consumerMetadata in dataLayer
    ...deepmerge({
      ...(await DataLayerTransformer.getConsumerMetadata(dataLayer))
    }, {
      consumerMetadata
    })
  });
}
export const sendPageEvent = async function (_temp, _ref2, option) {
  let {
    pageType,
    ...data
  } = _temp === void 0 ? {} : _temp;
  let {
    dataLayer,
    history
  } = _ref2;
  if (option === void 0) {
    option = {};
  }
  setTimeout(async () => {
    if (hasDom()) {
      if (!pageType) {
        throw new Error('Pixall: pageType is missing');
      }
      // option.initialConfig is set up in useAnalytics
      const eventPaths = _get(option, ['initialConfig', 'pixall', 'eventPaths'], {});
      const pageParams = _get(option, ['initialConfig', 'pixall', 'page'], {});
      const urlParams = new URLSearchParams(window.location.search);
      const {
        inventory,
        filterValues,
        consumerMetadata = {},
        noLabel = false,
        zipCode = '',
        isWalletPageload = false,
        ...rest
      } = data;
      const params = {
        pageType,
        ...DataLayerTransformer.getPageAttributes(dataLayer),
        ...(await transformData({
          inventory,
          filterValues,
          pageType,
          consumerMetadata,
          dataLayer
        })),
        ...eventPaths,
        ...pageParams,
        ...rest,
        listingType: inventory && inventory.priority
      };
      if (!isInitial) {
        params.type = 'event';
        params.eventType = 'ajaxPageView';
      } else {
        // send meta-data on pageload so that pixall can handle plage event for us
        params.type = 'meta-data';
        isInitial = false;
      }
      if (noLabel && params.label) {
        delete params.label;
      }

      // update search zip and user zip
      const queryZip = zipCode || (urlParams?.get('zip') ?? '');
      const {
        searchZip,
        userZip
      } = getZipCodes(dataLayer, queryZip);
      params.searchFacetZipCode = searchZip;
      params.zipCode = userZip;
      if (!isWalletPageload) {
        createCoxAutoDataLayer({
          dataLayer
        });
      }
      await ensurePixallPageLoadAndSendEvent(option, params, {
        history,
        isPageEvent: true
      });
    }
  }, 0);
};
export const sendClickEvent = async function (data, dataLayer, option) {
  if (option === void 0) {
    option = {};
  }
  setTimeout(async () => {
    if (hasDom()) {
      const {
        owner,
        inventory,
        filterValues,
        par,
        pixallData = {},
        hasListingType = true,
        pageType,
        consumerMetadata = {},
        cdl = {},
        zipCode = '',
        ...rest
      } = data;
      const pageId = DataLayerTransformer.getPageId(dataLayer);
      let listingType = pixallData.listingType || data.listingType || listingTypeUtils.getListingType(pageId, par);
      const urlParams = new URLSearchParams(window.location.search);
      if (pixallData?.clickTypeToListingType) {
        // clickType will be inventory?.clickType or pixallData.clickType or clickType from URL;
        const clickType = inventory?.clickType || pixallData.clickType || (urlParams ? urlParams.get('clickType') : undefined);
        listingType = listingTypeUtils.convertClickTypeToListingType(clickType);
      }
      transformCoxAutoDataLayer({
        listingType,
        cdl,
        dataLayer,
        eventType: 'click'
      });
      const params = {
        type: 'event',
        eventType: 'click',
        pageId,
        cdl,
        ...(await transformData({
          filterValues,
          inventory,
          owner,
          pageType,
          consumerMetadata,
          dataLayer
        })),
        ...rest
      };
      if (hasListingType) {
        params.listingType = listingType;
      } else {
        delete params?.listingType;
      }

      // update search zip and user zip
      const queryZip = zipCode || (urlParams?.get('zip') ?? '');
      const {
        searchZip,
        userZip
      } = getZipCodes(dataLayer, queryZip);
      params.searchFacetZipCode = searchZip;
      params.zipCode = userZip;
      await ensurePixallPageLoadAndSendEvent(option, params);
    }
  });
};
export const sendImpression = async function (_ref3, data, option) {
  let {
    dataLayer,
    history
  } = _ref3;
  if (option === void 0) {
    option = {};
  }
  setTimeout(async () => {
    const pageParams = DataLayerTransformer.getPageAttributes(dataLayer);
    if (hasDom()) {
      const {
        filterValues,
        clickType,
        hasListingType = true,
        consumerMetadata = {},
        inventory,
        includeInventory = false,
        cdl = {},
        zipCode = '',
        ...rest
      } = data;
      const urlParams = new URLSearchParams(window.location.search);
      const paramData = {
        filterValues,
        consumerMetadata,
        dataLayer
      };
      if (includeInventory) {
        paramData.inventory = inventory;
      }
      const listingType = data.listingType || listingTypeUtils.convertClickTypeToListingType(clickType);
      transformCoxAutoDataLayer({
        listingType,
        cdl,
        dataLayer,
        eventType: 'impression'
      });
      const params = {
        type: 'event',
        eventType: 'impression',
        cdl,
        ...pageParams,
        ...(await transformData(paramData)),
        ...rest
      };
      if (hasListingType) {
        params.listingType = listingType;
      }

      // update search zip and user zip
      const queryZip = zipCode || (urlParams?.get('zip') ?? '');
      const {
        searchZip,
        userZip
      } = getZipCodes(dataLayer, queryZip);
      params.searchFacetZipCode = searchZip;
      params.zipCode = userZip;
      await ensurePixallPageLoadAndSendEvent(option, params, {
        history
      });
    }
  }, 0);
};
export const updateCdl = data => {
  if (hasDom()) {
    window.coxAutoDataLayer = window.coxAutoDataLayer || [];
    window.coxAutoDataLayer.push(data);
  }
};
export default {
  sendPageEvent,
  sendClickEvent,
  sendImpression,
  updateCdl
};